import { useState, useEffect, lazy, Suspense } from 'react';
import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { UserProvider } from './contexts/UserContext';
import { useUser } from './contexts/UserContext';
import AppShell from './components/common/AppShell';
import ProtectedRoute from './components/common/ProtectedRoute';
import LoadingScreen from './components/screens/LoadingScreen';
import ConfirmedInBrowserNote from './components/common/ConfirmedInBrowserNote';
import { isSplashDone, markSplashDone } from './lib/splash';
import { getAuthLinkError } from './lib/authLinkError';
import WelcomeScreen from './components/screens/WelcomeScreen';
import OnboardingScreen from './components/screens/OnboardingScreen';
import GetVerifiedScreen from './components/screens/GetVerifiedScreen';
import SignInScreen from './components/screens/SignInScreen';
import EmailVerificationScreen from './components/screens/EmailVerificationScreen';
import ForgotPasswordScreen from './components/screens/ForgotPasswordScreen';
import ResetPasswordScreen from './components/screens/ResetPasswordScreen';
import FeedScreen from './components/screens/FeedScreen';
import VideoScreen from './components/screens/VideoScreen';
import CommunityScreen from './components/screens/CommunityScreen';
import ExploreScreen from './components/screens/ExploreScreen';
import RantRoomScreen from './components/screens/RantRoomScreen';
import GratitudeScreen from './components/screens/GratitudeScreen';
import ProfileScreen from './components/screens/ProfileScreen';
import PublicProfileScreen from './components/screens/PublicProfileScreen';
import UsernameRedirect from './components/screens/UsernameRedirect';
import SubmitVideoScreen from './components/screens/SubmitVideoScreen';
import NotificationsScreen from './components/screens/NotificationsScreen';
import ConnectionsScreen from './components/screens/ConnectionsScreen';
import PostDetailScreen from './components/screens/PostDetailScreen';

// Admin console (2026-09-24 redesign): one layout, a page per section.
const AdminLayout = lazy(() => import('./components/admin/AdminLayout'));
const AdminOverviewScreen = lazy(() => import('./components/screens/AdminOverviewScreen'));
const MembersAdminScreen = lazy(() => import('./components/screens/MembersAdminScreen'));
const VideoQueueScreen = lazy(() => import('./components/screens/VideoQueueScreen'));
const ReportsAdminScreen = lazy(() => import('./components/screens/ReportsAdminScreen'));
const InviteCodesScreen = lazy(() => import('./components/screens/InviteCodesScreen'));
const AgencyDomainsScreen = lazy(() => import('./components/screens/AgencyDomainsScreen'));
const VerificationQueueScreen = lazy(() => import('./components/screens/VerificationQueueScreen'));
const CuratedVideosScreen = lazy(() => import('./components/screens/CuratedVideosScreen'));
const EventsAdminScreen = lazy(() => import('./components/screens/EventsAdminScreen'));
const AnnouncementsAdminScreen = lazy(() => import('./components/screens/AnnouncementsAdminScreen'));
const BookshelfAdminScreen = lazy(() => import('./components/screens/BookshelfAdminScreen'));
const SignupsAdminScreen = lazy(() => import('./components/screens/SignupsAdminScreen'));
const TablesAdminScreen = lazy(() => import('./components/screens/TablesAdminScreen'));
const ClientErrorsScreen = lazy(() => import('./components/screens/ClientErrorsScreen'));
const FeedbackAdminScreen = lazy(() => import('./components/screens/FeedbackAdminScreen'));

const MIN_DISPLAY_MS = 3000;

const ADMIN_ROLES = ['moderator', 'admin', 'superadmin'];

function AdminRoute() {
  const { session, loading, userData } = useUser();
  if (loading) return null;
  if (!session) return <Navigate to="/welcome" replace />;
  if (!ADMIN_ROLES.includes(userData.app_role)) return <Navigate to="/feed" replace />;
  return <Outlet />;
}

// Waits for auth check before deciding where to send the user from "/".
// Without this, session is always null on first render (async) → always redirects to /welcome.
function RootRedirect() {
  const { session, loading, recoveryMode } = useUser();
  if (loading) return null;
  if (recoveryMode) return <Navigate to="/reset-password" replace />;
  // An expired / already-used email link: explain on sign-in, not a bare /welcome.
  if (!session && getAuthLinkError()) return <Navigate to="/login" replace state={{ linkError: true }} />;
  return <Navigate to={session ? '/feed' : '/welcome'} replace />;
}

// Wraps all routes — shows LoadingScreen overlay on every cold start (once per tab session).
// sessionStorage flag ensures it shows on fresh tab open but not on every in-app navigation.
function AppWithLoader() {
  const { loading } = useUser();
  const [showSplash, setShowSplash] = useState(
    () => !isSplashDone()
  );
  const [minTimeUp, setMinTimeUp] = useState(false);
  const [authChecked, setAuthChecked] = useState(!loading);
  const [fadingOut, setFadingOut] = useState(false);

  useEffect(() => {
    if (!showSplash) return;
    const t = setTimeout(() => setMinTimeUp(true), MIN_DISPLAY_MS);
    return () => clearTimeout(t);
  }, [showSplash]);

  useEffect(() => {
    if (!loading) setAuthChecked(true);
  }, [loading]);

  useEffect(() => {
    document.body.style.backgroundColor = showSplash ? '#1A2E22' : '';
  }, [showSplash]);

  useEffect(() => {
    if (showSplash && minTimeUp && authChecked && !fadingOut) {
      setFadingOut(true);
      setTimeout(() => {
        markSplashDone();
        setShowSplash(false);
      }, 400);
    }
  }, [showSplash, minTimeUp, authChecked, fadingOut]);

  return (
    <>
      <Routes>
        <Route path="/" element={<RootRedirect />} />
        <Route path="/welcome" element={<WelcomeScreen />} />
        <Route path="/onboarding" element={<OnboardingScreen />} />
        <Route path="/login" element={<SignInScreen />} />
        <Route path="/forgot-password" element={<ForgotPasswordScreen />} />
        <Route path="/reset-password" element={<ResetPasswordScreen />} />
        <Route path="/verify-email" element={<EmailVerificationScreen />} />
        <Route element={<ProtectedRoute />}>
          {/* Outside AppShell: onboarding's look, no nav. Where a new DSP lands
              once sign-up finishes (useFinishSignup). */}
          <Route path="/get-verified" element={<GetVerifiedScreen />} />
          <Route element={<AppShell />}>
            <Route path="/feed" element={<FeedScreen />} />
            <Route path="/video/:videoId" element={<VideoScreen />} />
            <Route path="/explore" element={<ExploreScreen />} />
            <Route path="/explore/:section" element={<ExploreScreen />} />
            <Route path="/explore/relax/rant-room" element={<RantRoomScreen />} />
            <Route path="/explore/relax/gratitude" element={<GratitudeScreen />} />
            <Route path="/community" element={<CommunityScreen />} />
            <Route path="/community/:tableName" element={<CommunityScreen />} />
            <Route path="/community/:tableName/:postId" element={<PostDetailScreen />} />
            <Route path="/profile" element={<ProfileScreen />} />
            <Route path="/profile/:userId" element={<PublicProfileScreen />} />
            <Route path="/u/:username" element={<UsernameRedirect />} />
            {/* Top-level, NOT /profile/connections — that would sit under the
                :userId param route above and read as a user id. Always shows
                the caller's OWN lists; it takes no user id by design. */}
            <Route path="/connections" element={<ConnectionsScreen />} />
            <Route path="/submit-video" element={<SubmitVideoScreen />} />
            <Route path="/notifications" element={<NotificationsScreen />} />
          </Route>
        </Route>
        <Route element={<ProtectedRoute />}>
          <Route element={<AppShell />}>
            <Route element={<AdminRoute />}>
              {/* AdminLayout supplies the sidebar and a Suspense boundary for
                  every page below; each page is still its own lazy chunk. */}
              <Route
                path="/admin"
                element={
                  <Suspense fallback={<div className="flex items-center justify-center h-64" style={{ color: 'var(--text-secondary)' }}>Loading…</div>}>
                    <AdminLayout />
                  </Suspense>
                }
              >
                <Route index element={<AdminOverviewScreen />} />
                <Route path="members" element={<MembersAdminScreen />} />
                <Route path="signups" element={<SignupsAdminScreen />} />
                <Route path="codes" element={<InviteCodesScreen />} />
                <Route path="domains" element={<AgencyDomainsScreen />} />
                <Route path="verification" element={<VerificationQueueScreen />} />
                <Route path="videos" element={<VideoQueueScreen />} />
                <Route path="reports" element={<ReportsAdminScreen />} />
                <Route path="tables" element={<TablesAdminScreen />} />
                <Route path="events" element={<EventsAdminScreen />} />
                <Route path="announcements" element={<AnnouncementsAdminScreen />} />
                <Route path="curated-videos" element={<CuratedVideosScreen />} />
                <Route path="bookshelf" element={<BookshelfAdminScreen />} />
                <Route path="errors" element={<ClientErrorsScreen />} />
                <Route path="feedback" element={<FeedbackAdminScreen />} />
              </Route>
            </Route>
          </Route>
        </Route>
      </Routes>
      <ConfirmedInBrowserNote />
      {showSplash && (
        <div className="fixed inset-0" style={{ zIndex: 9999, backgroundColor: '#1A2E22', transform: 'translateZ(0)', willChange: 'transform' }}>
          <LoadingScreen fadingOut={fadingOut} />
        </div>
      )}
    </>
  );
}

function App() {
  return (
    <UserProvider>
      <AppWithLoader />
    </UserProvider>
  );
}

export default App;
